import React, { useState, useEffect } from 'react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '../../../components/ui/dialog';
import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';
import Label from '../../../components/ui/label';
import { EXPENSE_CATEGORIES, VALIDATION_RULES } from '../shared/constants';

const ExpenseForm = ({ isOpen, onClose, onSubmit, initialData = null }) => {
	const [formData, setFormData] = useState({
		name: '',
		category: '',
		amount: '',
		date: '',
		notes: '',
	});
	const [errors, setErrors] = useState({});

	// Initialize form with data if editing
	useEffect(() => {
		if (initialData) {
			setFormData({
				name: initialData.name || '',
				category: initialData.category || '',
				amount: initialData.amount?.toString() || '',
				date: initialData.date || '',
				notes: initialData.notes || '',
			});
		} else {
			// Reset form when opening for new expense
			setFormData({
				name: '',
				category: '',
				amount: '',
				date: new Date().toISOString().split('T')[0], // Today's date
				notes: '',
			});
		}
		setErrors({});
	}, [initialData, isOpen]);

	const handleChange = (field, value) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		// Clear error when user starts typing
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: null }));
		}
	};

	const validate = () => {
		const newErrors = {};

		// Name validation
		if (!formData.name.trim()) {
			newErrors.name = 'Expense name is required';
		} else if (formData.name.length < VALIDATION_RULES.name.minLength) {
			newErrors.name = VALIDATION_RULES.name.message;
		}

		// Category validation
		if (!formData.category) {
			newErrors.category = VALIDATION_RULES.category.message;
		}

		// Amount validation
		const amount = parseFloat(formData.amount);
		if (!formData.amount || isNaN(amount)) {
			newErrors.amount = 'Amount is required';
		} else if (
			amount < VALIDATION_RULES.amount.min ||
			amount > VALIDATION_RULES.amount.max
		) {
			newErrors.amount = VALIDATION_RULES.amount.message;
		}

		// Date validation
		if (!formData.date) {
			newErrors.date = 'Date is required';
		}

		// Notes validation
		if (
			formData.notes &&
			formData.notes.length > VALIDATION_RULES.notes.maxLength
		) {
			newErrors.notes = VALIDATION_RULES.notes.message;
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		if (!validate()) return;

		onSubmit({
			...formData,
			amount: parseFloat(formData.amount),
		});

		onClose();
	};

	const handleCancel = () => {
		setFormData({
			name: '',
			category: '',
			amount: '',
			date: '',
			notes: '',
		});
		setErrors({});
		onClose();
	};

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className='max-w-md'>
				<DialogHeader>
					<DialogTitle className='text-2xl font-glacial text-clientBlack'>
						{initialData ? 'Edit Expense' : 'Add New Expense'}
					</DialogTitle>
				</DialogHeader>

				<form onSubmit={handleSubmit} className='space-y-4'>
					{/* Expense Name */}
					<div>
						<Label htmlFor='name' className='font-montserrat-medium'>
							Expense Name <span className='text-red-500'>*</span>
						</Label>
						<Input
							id='name'
							type='text'
							value={formData.name}
							onChange={(e) => handleChange('name', e.target.value)}
							placeholder='Facebook Ads - Q1 Campaign'
							className={`mt-1 font-montserrat ${
								errors.name ? 'border-red-500' : ''
							}`}
						/>
						{errors.name && (
							<p className='text-red-500 text-sm mt-1 font-montserrat'>
								{errors.name}
							</p>
						)}
					</div>

					{/* Category */}
					<div>
						<Label htmlFor='category' className='font-montserrat-medium'>
							Category <span className='text-red-500'>*</span>
						</Label>
						<select
							id='category'
							value={formData.category}
							onChange={(e) => handleChange('category', e.target.value)}
							className={`mt-1 w-full px-3 py-2 border rounded-md font-montserrat focus:outline-none focus:ring-2 focus:ring-primary ${
								errors.category ? 'border-red-500' : 'border-gray-300'
							}`}>
							<option value=''>Select a category</option>
							{EXPENSE_CATEGORIES.map((category) => (
								<option key={category} value={category}>
									{category}
								</option>
							))}
						</select>
						{errors.category && (
							<p className='text-red-500 text-sm mt-1 font-montserrat'>
								{errors.category}
							</p>
						)}
					</div>

					{/* Amount */}
					<div>
						<Label htmlFor='amount' className='font-montserrat-medium'>
							Amount <span className='text-red-500'>*</span>
						</Label>
						<div className='relative mt-1'>
							<span className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-montserrat'>
								$
							</span>
							<Input
								id='amount'
								type='number'
								step='0.01'
								min='0'
								value={formData.amount}
								onChange={(e) => handleChange('amount', e.target.value)}
								placeholder='500.00'
								className={`pl-7 font-montserrat ${
									errors.amount ? 'border-red-500' : ''
								}`}
							/>
						</div>
						{errors.amount && (
							<p className='text-red-500 text-sm mt-1 font-montserrat'>
								{errors.amount}
							</p>
						)}
					</div>

					{/* Date */}
					<div>
						<Label htmlFor='date' className='font-montserrat-medium'>
							Date <span className='text-red-500'>*</span>
						</Label>
						<Input
							id='date'
							type='date'
							value={formData.date}
							onChange={(e) => handleChange('date', e.target.value)}
							className={`mt-1 font-montserrat ${
								errors.date ? 'border-red-500' : ''
							}`}
						/>
						{errors.date && (
							<p className='text-red-500 text-sm mt-1 font-montserrat'>
								{errors.date}
							</p>
						)}
					</div>

					{/* Notes */}
					<div>
						<Label htmlFor='notes' className='font-montserrat-medium'>
							Notes <span className='text-gray-400'>(optional)</span>
						</Label>
						<textarea
							id='notes'
							value={formData.notes}
							onChange={(e) => handleChange('notes', e.target.value)}
							placeholder='Target: Age 35-55 in Greater Phoenix area'
							rows={3}
							className={`mt-1 w-full px-3 py-2 border rounded-md font-montserrat focus:outline-none focus:ring-2 focus:ring-primary resize-none ${
								errors.notes ? 'border-red-500' : 'border-gray-300'
							}`}
						/>
						{errors.notes && (
							<p className='text-red-500 text-sm mt-1 font-montserrat'>
								{errors.notes}
							</p>
						)}
						<p className='text-gray-500 text-xs mt-1 font-montserrat'>
							{formData.notes.length} / {VALIDATION_RULES.notes.maxLength}{' '}
							characters
						</p>
					</div>

					{/* Action Buttons */}
					<div className='flex justify-end gap-3 pt-4'>
						<Button
							type='button'
							onClick={handleCancel}
							variant='outline'
							className='font-montserrat'>
							Cancel
						</Button>
						<Button
							type='submit'
							className='bg-primary hover:bg-primary/90 text-white font-montserrat'>
							{initialData ? 'Save Changes' : 'Add Expense'}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default ExpenseForm;
